import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock, Loader2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getToken } from '../utils/tokenManager';

const API = process.env.REACT_APP_BACKEND_URL;
const IDLE_MS = 5 * 60 * 1000; // 5 minutes — per founder decision (mandatory)
const CHECK_MS = 15 * 1000;    // sample every 15s

/**
 * AutoLockOverlay — invisible until idle for 5 minutes, then covers
 * the whole app with a blur + password prompt. Only ships when the
 * member is authenticated. Any of the following counts as activity
 * (per founder Ch. 5 clarification): mouse move, key press, touch,
 * scroll, or the browser tab becoming visible again.
 */
const AutoLockOverlay = () => {
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const lastActivityRef = useRef(Date.now());
  const isAuthed = !!getToken();

  const bump = useCallback(() => { lastActivityRef.current = Date.now(); }, []);

  // Wire activity listeners once.
  useEffect(() => {
    if (!isAuthed) return undefined;
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const onVisibility = () => { if (!document.hidden) bump(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthed, bump]);

  // Sample the idle timer.
  useEffect(() => {
    if (!isAuthed) return undefined;
    const id = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_MS) setLocked(true);
    }, CHECK_MS);
    return () => clearInterval(id);
  }, [isAuthed]);

  const unlock = useCallback(async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/verify-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setLocked(false);
        setPassword('');
        bump();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Incorrect password. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [password, bump]);

  if (!isAuthed || !locked) return null;

  return (
    <div
      data-testid="auto-lock-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-2xl bg-slate-900/70"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={unlock}
        className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl p-6 space-y-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <Lock className="w-5 h-5 text-emerald-700" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-lg font-serif font-bold text-slate-900">Session paused</h2>
            <p className="text-sm text-slate-500">Enter your password to continue.</p>
          </div>
        </div>
        <Input
          type="password"
          data-testid="auto-lock-password-input"
          autoFocus
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          placeholder="Your password"
          disabled={submitting}
        />
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600" data-testid="auto-lock-error">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}
        <Button
          type="submit"
          data-testid="auto-lock-submit"
          disabled={submitting || !password}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Unlocking…</>) : 'Continue'}
        </Button>
      </form>
    </div>
  );
};

export default AutoLockOverlay;
